import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik, FormikErrors } from "formik";
import { useCallback, useMemo, useState } from "react";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import { updatePaymentTemplate } from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { validateWithZod } from "src/services/formValidations";
import {
  emptyPaymentTemplateForm,
  PaymentTemplateFormData,
  PaymentTemplateFormSchema,
} from "./paymentForm";
import { Payment } from "./types";

interface EditPaymentDrawerProps {
  payment: Payment | null;
  isOpen: boolean;
  onClose: () => void;
}

const FORM_ID = "edit-payment-form";

export const EditPaymentDrawer = ({
  payment,
  isOpen,
  onClose,
}: EditPaymentDrawerProps) => {
  const toast = useAvelaToast();
  const queryClient = useQueryClient();
  // The drawer body unmounts on close, so Formik state resets on reopen.
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues = useMemo<PaymentTemplateFormData>(
    () =>
      payment
        ? {
            name: payment.name,
            description: payment.description,
            amount: payment.amount.toFixed(2),
          }
        : emptyPaymentTemplateForm,
    [payment]
  );

  const validate = useCallback(
    async (
      values: PaymentTemplateFormData
    ): Promise<FormikErrors<PaymentTemplateFormData> | undefined> => {
      const errors = await validateWithZod(PaymentTemplateFormSchema)(values);
      setIsFormValid(errors === undefined);
      return errors;
    },
    []
  );

  const handleSubmit = useCallback(
    async (values: PaymentTemplateFormData) => {
      if (!payment) return;

      const amountInCents = Math.round(parseFloat(values.amount) * 100);
      setIsSubmitting(true);
      try {
        await updatePaymentTemplate({
          path: { templateId: payment.id },
          body: {
            name: values.name,
            description: values.description || undefined,
            amountInCents,
          },
        });
        toast({
          title: "Success",
          description: "Payment updated successfully",
          status: "success",
        });
        queryClient.invalidateQueries({ queryKey: ["paymentTemplates"] });
        queryClient.invalidateQueries({ queryKey: ["payments"] });
        onClose();
      } catch (error) {
        console.error("Failed to update payment:", error);
        toast({
          title: "Error",
          description: "Failed to update payment",
          status: "error",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [payment, toast, queryClient, onClose]
  );

  return (
    <ResizableDrawer
      isOpen={isOpen}
      onClose={onClose}
      header="Edit payment"
      contentProps={{ bg: "white" }}
      footer={
        <HStack spacing={2} w="full">
          <Button
            variant="outline"
            flex={1}
            onClick={onClose}
            colorScheme="gray"
          >
            Cancel
          </Button>
          <Button
            colorScheme="primary"
            flex={1}
            type="submit"
            form={FORM_ID}
            isDisabled={!isFormValid}
            isLoading={isSubmitting}
          >
            Update payment
          </Button>
        </HStack>
      }
    >
      <Formik<PaymentTemplateFormData>
        initialValues={initialValues}
        enableReinitialize
        validate={validate}
        validateOnMount
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <Form id={FORM_ID}>
            <VStack spacing={6} align="stretch">
              <FormControl
                isRequired
                isInvalid={Boolean(formik.touched.name && formik.errors.name)}
              >
                <FormLabel
                  htmlFor="name"
                  fontSize="sm"
                  fontWeight="medium"
                  color="gray.700"
                  mb={1.5}
                >
                  Payment name
                </FormLabel>
                <Input
                  id="name"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="E.g. Uniform fee"
                  size="md"
                  bg="white"
                />
                {formik.touched.name && formik.errors.name ? (
                  <FormErrorMessage fontSize="xs" mt={1.5}>
                    {formik.errors.name}
                  </FormErrorMessage>
                ) : (
                  <FormHelperText fontSize="xs" color="gray.500" mt={1.5}>
                    Visible to staff and families
                  </FormHelperText>
                )}
              </FormControl>

              <FormControl>
                <FormLabel
                  htmlFor="description"
                  fontSize="sm"
                  fontWeight="medium"
                  color="gray.700"
                  mb={1.5}
                >
                  Payment description
                </FormLabel>
                <Textarea
                  id="description"
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder=""
                  size="md"
                  bg="white"
                  rows={4}
                />
                <FormHelperText fontSize="xs" color="gray.500" mt={1.5}>
                  Visible to staff and families
                </FormHelperText>
              </FormControl>

              <FormControl
                isRequired
                isInvalid={Boolean(
                  formik.touched.amount && formik.errors.amount
                )}
              >
                <FormLabel
                  htmlFor="amount"
                  fontSize="sm"
                  fontWeight="medium"
                  color="gray.700"
                  mb={1.5}
                >
                  Payment amount
                </FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none" color="gray.500">
                    $
                  </InputLeftElement>
                  <Input
                    id="amount"
                    name="amount"
                    value={formik.values.amount}
                    onChange={(e) =>
                      formik.setFieldValue("amount", e.target.value)
                    }
                    onBlur={formik.handleBlur}
                    placeholder="Enter an amount"
                    type="number"
                    step="0.01"
                    min="0"
                    size="md"
                    bg="white"
                  />
                </InputGroup>
                {formik.touched.amount && formik.errors.amount ? (
                  <FormErrorMessage fontSize="xs" mt={1.5}>
                    {formik.errors.amount}
                  </FormErrorMessage>
                ) : (
                  <FormHelperText fontSize="xs" color="gray.500" mt={1.5}>
                    Will be collected in USD, currencies set up in Stripe.{" "}
                    <Link
                      href="https://avelahelp.zendesk.com/hc/en-us/articles/47341300235021"
                      color="primary.500"
                      textDecoration="underline"
                      isExternal
                    >
                      Learn more.
                    </Link>
                  </FormHelperText>
                )}
              </FormControl>
            </VStack>
          </Form>
        )}
      </Formik>
    </ResizableDrawer>
  );
};
